import APP_CONFIG from '@/config/app';
import useOnClickOrScrollOutside from '@/lib/hooks/useOnClickOrScrollOutside';
import { logError, logInfo } from '@/utils/logger';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  CSSProperties,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import FormInput, { FormInputProps } from '../Input/FormInput';
const { MAIN_ROUNDEDNESS, MAIN_PADDING, MAIN_GAP, ALTERNATIVE_BACKDROP_BLUR } =
  APP_CONFIG.STYLES;
const dropDownSpacingClasses = {
  dropDownPadding: `p${MAIN_PADDING}`,
  dropDownGap: `gap${MAIN_GAP}`,
  dropDownSelectedOptionPadding: `inset-0 mt-sm-wholestep ml${MAIN_PADDING}`,
};
const dropDownSizingClasses = {
  dropDownWidth: 'w-full',
  dropDownMaxHeight: 'max-h-96',
  dropDownMinWidth: 'min-w-52',
};
const dropDownColorClasses = {
  dropDownBackground: `bg-primary/5 backdrop-blur${ALTERNATIVE_BACKDROP_BLUR}`,
  dropDownBorder: 'border border-solid border-primary/5',
};
const dropDownRoundednessClasses = {
  dropdownRounded: `rounded${MAIN_ROUNDEDNESS}`,
};
export type OptionValue = number | string;
export type DropDownSelectBoxOption<TValue extends OptionValue> = {
  value: TValue;
};
export type Props<TValue extends OptionValue> = {
  options: DropDownSelectBoxOption<TValue>[];
  displayOptions: ReactNode[];
  selectedDisplayOptions?: ReactNode[];
  onChange?: (value: DropDownSelectBoxOption<TValue>) => void;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors?: FieldErrors;
  clearErrors?: UseFormClearErrors<any>;
  label?: string;
  value?: OptionValue;
  description?: string;
  extraButton?: ReactNode;
  extraButtonPosition?: 'top' | 'bottom';
  closeOnExtraButtonClick?: boolean;
  zIndex?: number;
  closeDropdown?: boolean;
  preventCloseOnOutsideClick?: boolean;
};
export interface DropDownRef {
  setSelectedOption: (value: OptionValue) => void;
}
const DropDownSelectBox = forwardRef<
  DropDownRef,
  FormInputProps<any> & Props<OptionValue>
>(
  (
    {
      name,
      intent,
      options,
      displayOptions,
      selectedDisplayOptions,
      onChange,
      setValue,
      register,
      errors,
      clearErrors,
      label,
      value,
      description,
      extraButton,
      extraButtonPosition = 'bottom',
      closeOnExtraButtonClick = false,
      zIndex = 20,
      closeDropdown = false,
      preventCloseOnOutsideClick = false,
      ...props
    },
    ref,
  ) => {
    const toggleLockRef = useRef(false);
    const selectedValueRef = useRef<OptionValue | undefined>(value);
    const [selectedValue, setSelectedValue] = useState<OptionValue | undefined>(
      value,
    );
    const [toggle, setToggle] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
    const handleToggle = useCallback(() => {
      if (toggleLockRef.current) return;
      setToggle((prev) => !prev);
      toggleLockRef.current = true;
      setTimeout(() => {
        toggleLockRef.current = false;
      }, 200);
    }, []);
    const handleSelect = useCallback(
      (value: OptionValue, index: number) => {
        if (value === undefined) {
          logError(
            new Error('Undefined value encountered in handleSelect'),
            'DropDownSelectBox.handleSelect',
            { index },
          );
          return;
        }
        setSelectedValue(value);
        selectedValueRef.current = value;
        onChange?.({ value });
        setValue?.(name, value, { shouldValidate: true, shouldDirty: true });
        setToggle(false);
      },
      [onChange, setValue, name],
    );
    const calculateDropdownPosition = useCallback(() => {
      const referenceElement = containerRef.current;
      if (!referenceElement) return;
      const rect = referenceElement.getBoundingClientRect();
      const newStyle: CSSProperties = {
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        minWidth: rect.width,
        maxWidth: rect.width,
        position: 'absolute',
        zIndex: zIndex,
      };
      setDropdownStyle((prev) =>
        JSON.stringify(prev) === JSON.stringify(newStyle) ? prev : newStyle,
      );
    }, []);
    useOnClickOrScrollOutside(containerRef, (event) => {
      if (preventCloseOnOutsideClick) return;
      if (extraButton && dropdownRef.current?.contains(event.target as Node)) {
        if (closeOnExtraButtonClick) {
          setToggle(false);
        }
        return;
      }
      if (toggle) setToggle(false);
      if (clearErrors) clearErrors(name);
    });
    useImperativeHandle(ref, () => ({
      setSelectedOption(value: OptionValue) {
        logInfo('setSelectedOption called', 'DropDownSelectBox', { value });
        setSelectedValue(value);
        selectedValueRef.current = value;
      },
    }));
    useEffect(() => {
      if (value !== undefined && value !== selectedValueRef.current) {
        setSelectedValue(value);
        selectedValueRef.current = value;
      }
    }, [value]);
    useEffect(() => {
      if (toggle) {
        calculateDropdownPosition();
        const resizeHandler = () => calculateDropdownPosition();
        window.addEventListener('resize', resizeHandler);
        return () => window.removeEventListener('resize', resizeHandler);
      }
    }, [toggle, calculateDropdownPosition]);
    useEffect(() => {
      if (closeDropdown) {
        setToggle(false);
      }
    }, [closeDropdown]);
    const selectedOptionIndex = useMemo(
      () => options.findIndex((option) => option.value === selectedValue),
      [options, selectedValue],
    );
    const slicedDisplayOptions = useMemo(
      () => displayOptions.slice(0, 100),
      [displayOptions],
    );
    const MemoizedDropdownItem = React.memo(
      ({
        children,
        onSelect,
      }: {
        children: ReactNode;
        onSelect: () => void;
      }) => {
        return (
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect();
            }}
            className="flex items-center cursor-pointer transform-gpu scroll-snap-align-start transition-all duration-200 ease-out"
          >
            {children}
          </div>
        );
      },
    );
    MemoizedDropdownItem.displayName = 'MemoizedDropdownItem';
    const renderDropdownOptions = useMemo(() => {
      if (typeof document === 'undefined' || !toggle) return null;
      return createPortal(
        <AnimatePresence>
          <motion.div
            initial={APP_CONFIG.ANIMATION_SETTINGS.DROPDOWN.initial}
            animate={APP_CONFIG.ANIMATION_SETTINGS.DROPDOWN.open}
            exit={APP_CONFIG.ANIMATION_SETTINGS.DROPDOWN.closed}
            transition={APP_CONFIG.ANIMATION_SETTINGS.DROPDOWN.transition}
            className={`flex flex-col 
              ${dropDownSizingClasses.dropDownMinWidth} 
              ${dropDownRoundednessClasses.dropdownRounded} 
              ${dropDownColorClasses.dropDownBackground} 
              ${dropDownColorClasses.dropDownBorder} 
              shadow-md overflow-hidden
            `}
            style={{ ...dropdownStyle, zIndex: zIndex }}
            ref={dropdownRef}
          >
            {extraButton && extraButtonPosition === 'top' && (
              <div
                className={`sticky top px${MAIN_PADDING} pb${MAIN_PADDING} pt-0`}
              >
                {extraButton}
              </div>
            )}
            <div
              className={`
                ${dropDownSizingClasses.dropDownMaxHeight} 
                overflow-y-auto flex flex-col
                ${dropDownSpacingClasses.dropDownPadding} 
                ${dropDownSpacingClasses.dropDownGap}
              `}
            >
              {slicedDisplayOptions.map((option, index) => {
                const optionValue = options[index]?.value;
                return (
                  <MemoizedDropdownItem
                    key={optionValue ?? index}
                    onSelect={() => {
                      if (optionValue !== undefined) {
                        handleSelect(optionValue, index);
                      }
                    }}
                  >
                    {option}
                  </MemoizedDropdownItem>
                );
              })}
            </div>
            {extraButton && extraButtonPosition !== 'top' && (
              <div
                className={`sticky bottom px${MAIN_PADDING} pb${MAIN_PADDING} pt-0`}
              >
                {extraButton}
              </div>
            )}
          </motion.div>
        </AnimatePresence>,
        document.body,
      );
    }, [
      toggle,
      dropdownStyle,
      slicedDisplayOptions,
      options,
      handleSelect,
      extraButton,
      extraButtonPosition,
    ]);
    return (
      <div className="relative w-full" ref={containerRef}>
        <FormInput
          size="dropdown"
          name={name}
          label={label}
          description={description}
          suffixIconName={toggle ? 'chevronUp' : 'chevronDown'}
          onIconClick={handleToggle}
          onClick={handleToggle}
          onFocus={handleToggle}
          intent={errors?.[name] ? 'error' : intent}
          errorMessage={errors?.[name]?.message}
          clearErrors={clearErrors}
          {...props}
        />
        {selectedValue && (
          <div
            className={`absolute flex items-center pointer-events-none ${dropDownSpacingClasses.dropDownSelectedOptionPadding}`}
          >
            {selectedDisplayOptions?.[selectedOptionIndex]}
          </div>
        )}
        {renderDropdownOptions}
      </div>
    );
  },
);
DropDownSelectBox.displayName = 'DropDownSelectBox';
export default DropDownSelectBox;
